import { GRASS_CHARACTERISTICS, GRASS_CHARACTERISTICS_LAST_VERIFIED } from '@/data/grass-characteristics'
import { getGrassReferenceImages, type ReferenceImage } from '@/data/grass-reference-photos'
import { GUIDE_METADATA } from '@/data/guide-slugs'
import { GRASS_ATLAS_MORPHOLOGY, getAtlasMorphology, type AtlasMorphologySource } from '@/data/grass-atlas-morphology'
import { KENTUCKY_BLUEGRASS_MODEL_SOURCES } from './kentucky-bluegrass-model'
import { GRASS_MODEL_REGISTRY, getProceduralGrassModel } from './models/grass-model-registry'

export interface AtlasSpecies {
  slug: string
  name: string
  grassKey: string
  season: 'cool' | 'warm'
  zones: string
  summary: string
  color: string
  bladeWidth: string
  bladeShape: string
  texture: string
  density: string
  growthHabit: string
  rootSystem: string
  seedhead: string
  photoIdCues: string[]
  lookalikes: { grass: string; tell: string }[]
  lastVerified: string
  modelSources: AtlasMorphologySource[]
  modelLimitations: string[]
  referenceImages: ReferenceImage[]
  simulationBaseline: GrassSimulationBaseline
}

export interface GrassSimulationBaseline {
  establishmentDays: { min: number; max: number }
  mowingInches: { minimum: number; optimumMin: number; optimumMax: number }
  rootDepthInches: number
  summerWaterInches: number
}

const NUMBER_PATTERN = /\d+(?:\.\d+)?/g

/**
 * Atlas inventory comes from reviewed morphology and model records. Guide
 * metadata supplies page copy for those records, but it cannot add a new
 * Atlas entry by itself.
 */
const ATLAS_MODEL_RECORD_SLUGS = new Set([
  'kentucky-bluegrass',
  ...Object.keys(GRASS_MODEL_REGISTRY),
])

export const ATLAS_CATALOGUE_SLUGS = Object.keys(GRASS_ATLAS_MORPHOLOGY).filter((slug) =>
  ATLAS_MODEL_RECORD_SLUGS.has(slug),
)

export function getAllAtlasSlugs(): string[] {
  return ATLAS_CATALOGUE_SLUGS.slice()
}

export function isAtlasSpeciesSlug(slug: string): boolean {
  return ATLAS_CATALOGUE_SLUGS.includes(slug)
}

export function parseDayRange(value: string | undefined, fallback = { min: 14, max: 28 }) {
  const values = value?.match(NUMBER_PATTERN)?.map(Number).filter(Number.isFinite) ?? []
  if (values.length === 0) return fallback
  const min = Math.max(1, values[0]!)
  const max = Math.max(min, values[1] ?? min)
  return { min, max }
}

export function parseInchRange(value: string | undefined, fallback = { min: 2, max: 3 }) {
  const values = value?.match(NUMBER_PATTERN)?.map(Number).filter(Number.isFinite) ?? []
  if (values.length === 0) return fallback
  const min = Math.max(0.5, values[0]!)
  const max = Math.max(min, values[1] ?? min)
  return { min, max }
}

function simulationBaselineFor(grass: (typeof GRASS_CHARACTERISTICS)[string]): GrassSimulationBaseline {
  const establishmentDays = parseDayRange(grass.establishmentTime)
  const optimum = parseInchRange(grass.mowingDetails?.optimal ?? grass.mowingHeight)
  const minimumRange = parseInchRange(grass.mowingDetails?.minimum, { min: Math.max(0.5, optimum.min * 0.7), max: Math.max(0.5, optimum.min * 0.7) })
  return {
    establishmentDays,
    mowingInches: {
      minimum: minimumRange.min,
      optimumMin: optimum.min,
      optimumMax: optimum.max,
    },
    rootDepthInches: Math.max(6, grass.wateringNeeds?.rootDepth ?? 18),
    summerWaterInches: Math.max(0.25, grass.wateringNeeds?.weeklyRequirements.summer ?? 1),
  }
}

export function getAtlasSpecies(): AtlasSpecies[] {
  return ATLAS_CATALOGUE_SLUGS.flatMap((slug) => {
    const guide = GUIDE_METADATA.find((candidate) => candidate.slug === slug)
    if (!guide) return []

    const grass = GRASS_CHARACTERISTICS[guide.grassKey]
    if (!grass) return []

    const profile = getAtlasMorphology(guide.slug)
    const model = getProceduralGrassModel(guide.slug)

    return [{
      slug: guide.slug,
      name: guide.displayName,
      grassKey: guide.grassKey,
      season: guide.season,
      zones: guide.zones,
      summary: guide.shortDescription,
      color: grass.color,
      bladeWidth: grass.bladeWidth,
      bladeShape: grass.bladeShape,
      texture: grass.texture,
      density: grass.density,
      growthHabit: grass.growthHabit,
      rootSystem: grass.rootSystem,
      seedhead: grass.seedhead ?? 'Seedheads are uncommon in a regularly mowed lawn.',
      photoIdCues: grass.photoIdCues ?? [],
      lookalikes: grass.lookalikes ?? [],
      lastVerified: GRASS_CHARACTERISTICS_LAST_VERIFIED,
      modelSources:
        guide.slug === 'kentucky-bluegrass'
          ? KENTUCKY_BLUEGRASS_MODEL_SOURCES.map((item) => ({
              publisher: item.label,
              label: item.label,
              href: item.href,
            }))
          : model?.sources ?? profile.sources,
      modelLimitations: model?.limitations ?? [profile.reviewNote],
      referenceImages: getGrassReferenceImages(guide.grassKey),
      simulationBaseline: simulationBaselineFor(grass),
    }]
  })
}

export function getAtlasSpeciesBySlug(slug: string): AtlasSpecies | undefined {
  return getAtlasSpecies().find((species) => species.slug === slug)
}
